import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLatest } from 'ahooks';
import type { ValueOf } from 'type-fest';

// Matches WebSocket.readyState's numbering, but only the values this hook's own `readyState`
// actually transitions through - it never surfaces an observable Closing state.
export const READY_STATE = {
  Connecting: 0,
  Open: 1,
  Closed: 3,
} as const;

export type ReadyState = ValueOf<typeof READY_STATE>;

/**
 * Decides whether an unexpected drop should be retried. Return `false` to make the close
 * terminal.
 */
export type ReconnectPolicy = (event: CloseEvent) => boolean;

export type UseWebsocketOptions = {
  onOpen?: (event: Event, instance: WebSocket) => void;
  /**
   * Also fires for a connection torn down by an unmount, a url change, or React 18 StrictMode's
   * mount->cleanup->mount, so it isn't a reliable "the connection was lost" signal on its own.
   */
  onClose?: (event: CloseEvent, instance: WebSocket) => void;
  onMessage: (event: MessageEvent, instance: WebSocket) => void;
  onError?: (event: Event, instance: WebSocket) => void;
  /**
   * Reconnect with exponential backoff after an unexpected drop. Defaults to `true`.
   *
   * A boolean applies the default policy: retry every drop except a clean close (code 1000),
   * matching the retired `particles-js-websocket-wrapper`. Pass a predicate to decide per close
   * event instead - `(event) => event.code !== 4001` is the escape hatch for a server that closes
   * with 1000 on a graceful restart, where the default would strand every client until a reload.
   */
  reconnect?: boolean | ReconnectPolicy;
};

type SendData = Parameters<WebSocket['send']>[0];

// Where a single effect instance is in its lifecycle. Only ever moves forward, so the states a
// reader has to hold in their head are exactly these three.
type Phase = 'active' | 'terminal' | 'tornDown';

export type UseWebsocketResult = {
  readyState: ReadyState;
  disconnect: () => void;
  /** Sends immediately if open, otherwise queues (bounded) until the next open. */
  send: (data: SendData) => void;
};

// Exponential backoff from 50ms up to 60s, only reset once a connection survives `STABLE_OPEN_MS`.
// Resetting on every open would mean a server that's flapping - accepting a connection and
// dropping it again almost immediately - never sees the client back off.
const MIN_RECONNECT_INTERVAL_MS = 50;
const MAX_RECONNECT_INTERVAL_MS = 60_000;
const RECONNECT_DECAY_FACTOR = 2;
const STABLE_OPEN_MS = 5_000;

// Caps how many outbound messages pile up while disconnected (initial connect, or a reconnect
// gap) - without a cap, an outage that outlasts a few retries would grow the queue unbounded.
// Drops the oldest queued message once full.
const MAX_QUEUED_MESSAGES = 1_000;

// Equal jitter: scales the nominal backoff into [50%, 100%] of itself, so clients dropped by the
// same event (a gateway restart, a deploy) don't all retry on the exact same schedule.
const withJitter = (delayMs: number) => delayMs / 2 + Math.random() * (delayMs / 2);

// Queueing a send that nothing will ever drain reads to the caller like the send worked, so the
// unsendable cases say so instead of swallowing the data.
const warnUndeliverableSend = (reason: string) =>
  // eslint-disable-next-line no-console -- a dropped message is a caller bug worth surfacing, and this module has no logger dependency
  console.warn(`useWebsocket: dropping send() because ${reason}`);

/**
 * Owns a single WebSocket connection: opens it, reconnects with jittered exponential backoff
 * after an unexpected drop, and exposes just enough state for consumers to build on top of.
 */
export function useWebsocket(socketUrl: string | undefined, options: UseWebsocketOptions): UseWebsocketResult {
  const onOpenRef = useLatest(options.onOpen);
  const onCloseRef = useLatest(options.onClose);
  const onMessageRef = useLatest(options.onMessage);
  const onErrorRef = useLatest(options.onError);
  // Read through a ref rather than an effect dependency: it only feeds the close handler's retry
  // decision, so making it a dependency would drop a live connection just to change the policy.
  const reconnectRef = useLatest<boolean | ReconnectPolicy>(options.reconnect ?? true);

  const [readyState, setReadyState] = useState<ReadyState>(socketUrl ? READY_STATE.Connecting : READY_STATE.Closed);
  const disconnectRef = useRef<() => void>(() => {});
  const disconnect = useCallback(() => disconnectRef.current(), []);
  const sendRef = useRef<(data: SendData) => void>(() => warnUndeliverableSend('the hook has not mounted yet'));
  const send = useCallback((data: SendData) => sendRef.current(data), []);

  useEffect(() => {
    if (!socketUrl) {
      setReadyState(READY_STATE.Closed);
      // Point the imperative handles at this connection-less instance. Leaving them bound to the
      // previous instance would aim `send` at a queue nothing drains and `disconnect` at a socket
      // that's already closed.
      disconnectRef.current = () => {};
      sendRef.current = () => warnUndeliverableSend('the socket url is empty');
      return undefined;
    }

    let socket: WebSocket | undefined;
    // `terminal`: nothing will reopen this socket - a manual disconnect, a close the reconnect
    // policy declined, or a url the browser rejects outright.
    // `tornDown`: set synchronously in the cleanup below, before closing the socket, so a close
    // event that arrives for a superseded instance (unmount, url change, or React 18 StrictMode's
    // mount->cleanup->mount) never mutates state or schedules a backoff retry.
    let phase: Phase = 'active';
    let nominalDelay: number | undefined;
    let backoffTimer: ReturnType<typeof setTimeout> | undefined;
    let stableTimer: ReturnType<typeof setTimeout> | undefined;
    // Reset on a successful open, so a flapping connection surfaces one `onError` per outage
    // instead of one per retry attempt - a reconnecting consumer forwarding this into a user-facing
    // toast would otherwise re-alert on every failed attempt for as long as the outage lasts.
    let hasNotifiedErrorSinceOpen = false;
    // Persists across reconnects within this effect instance (a drop-and-retry shouldn't lose
    // queued sends), but not across url changes - a new url gets a fresh effect instance and an
    // empty queue rather than replaying stale sends meant for the old connection.
    const messageQueue: Array<SendData> = [];

    const clearBackoffTimer = () => {
      if (backoffTimer !== undefined) {
        clearTimeout(backoffTimer);
        backoffTimer = undefined;
      }
    };

    const clearStableTimer = () => {
      if (stableTimer !== undefined) {
        clearTimeout(stableTimer);
        stableTimer = undefined;
      }
    };

    const shouldReconnect = (event: CloseEvent) => {
      const policy = reconnectRef.current;
      return typeof policy === 'function' ? policy(event) : policy && event.code !== 1000;
    };

    const open = () => {
      let ws: WebSocket;
      try {
        ws = new WebSocket(socketUrl);
      } catch (error) {
        // The constructor throws synchronously for a url the browser won't accept at all - a bad
        // scheme, a fragment, mixed content, a CSP block. All permanent, so retrying can't help,
        // and letting it escape a backoff timer would leave a zombie: no socket, no pending retry.
        phase = 'terminal';
        setReadyState(READY_STATE.Closed);
        // eslint-disable-next-line no-console -- an unusable url is a caller bug, and this module has no logger dependency
        console.error(`useWebsocket: cannot open ${socketUrl}`, error);
        return;
      }

      socket = ws;
      setReadyState(READY_STATE.Connecting);

      // These three guard on `phase !== 'active'` rather than just `tornDown`: a socket event is
      // queued as a task when it happens, so a `disconnect()` landing before that task runs still
      // sees the event dispatched afterwards. Reporting an open (or a message, or an error) for a
      // connection the caller already gave up on is worse than dropping it. The close handler
      // below is deliberately not like this - it needs to tell the two non-active states apart.
      ws.addEventListener('open', (event) => {
        if (socket !== ws || phase !== 'active') return;

        hasNotifiedErrorSinceOpen = false;
        setReadyState(READY_STATE.Open);
        clearStableTimer();
        stableTimer = setTimeout(() => {
          nominalDelay = undefined;
        }, STABLE_OPEN_MS);

        try {
          // Drained one at a time, taking each message off before sending it, so a `send` that
          // throws (an `InvalidStateError` for a socket that died mid-flush, a bad payload) costs
          // only that message instead of the whole batch. The catch keeps the throw from escaping
          // this listener into window.onerror, and leaves the untried remainder queued for the
          // next open rather than draining the whole backlog into the console.
          let next = messageQueue.shift();
          while (next !== undefined) {
            ws.send(next);
            next = messageQueue.shift();
          }
        } catch (error) {
          // eslint-disable-next-line no-console -- a rejected message is a caller bug, and this module has no logger dependency
          console.error('useWebsocket: dropping a queued message the socket rejected', error);
        }

        onOpenRef.current?.(event, ws);
      });

      ws.addEventListener('close', (event) => {
        if (socket !== ws) return;

        // Fires even for a superseded/torn-down instance's close, matching this hook's previous
        // (reviewed) behavior - callers that need to distinguish should check `phase`-adjacent
        // signals themselves; nothing today does.
        onCloseRef.current?.(event, ws);

        if (phase === 'tornDown') return;

        setReadyState(READY_STATE.Closed);
        clearStableTimer();

        // Already terminal means disconnect() asked for this close.
        if (phase === 'terminal' || !shouldReconnect(event)) {
          phase = 'terminal';
          return;
        }

        nominalDelay =
          nominalDelay === undefined
            ? MIN_RECONNECT_INTERVAL_MS
            : Math.min(nominalDelay * RECONNECT_DECAY_FACTOR, MAX_RECONNECT_INTERVAL_MS);

        clearBackoffTimer();
        backoffTimer = setTimeout(open, withJitter(nominalDelay));
      });

      ws.addEventListener('message', (event) => {
        if (socket === ws && phase === 'active') onMessageRef.current?.(event, ws);
      });

      ws.addEventListener('error', (event) => {
        if (socket !== ws || phase !== 'active' || hasNotifiedErrorSinceOpen) return;
        hasNotifiedErrorSinceOpen = true;
        onErrorRef.current?.(event, ws);
      });
    };

    open();

    disconnectRef.current = () => {
      phase = 'terminal';
      clearBackoffTimer();
      clearStableTimer();
      socket?.close();
    };

    sendRef.current = (data) => {
      if (socket?.readyState === WebSocket.OPEN) {
        socket.send(data);
        return;
      }

      if (phase !== 'active') {
        warnUndeliverableSend('the connection is closed and will not reopen');
        return;
      }

      messageQueue.push(data);
      if (messageQueue.length > MAX_QUEUED_MESSAGES) messageQueue.shift();
    };

    return () => {
      phase = 'tornDown';
      clearBackoffTimer();
      clearStableTimer();
      socket?.close();
    };
    // The refs are ahooks useLatest refs - stable across renders like useRef, so listing them
    // satisfies exhaustive-deps without ever re-running the effect. `socketUrl` is the only
    // dependency that actually reopens the connection.
  }, [socketUrl, onOpenRef, onCloseRef, onMessageRef, onErrorRef, reconnectRef]);

  return useMemo(() => ({ readyState, disconnect, send }), [readyState, disconnect, send]);
}
